import Editor, { loader } from "@monaco-editor/react";
import { ProductTemplatesApi } from "api/ProductTemplatesApi";
import { Button } from "primereact/button";
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { useEffect, useState } from 'react';
import { CellType } from 'utils/enums';
import {
	IProductTemplate,
	IProductTemplateAttribute,
	IProductTemplateConfig,
} from '../../dashboard/product-template-table/utils/interfaces';
import { easasAlert } from "../easas-global-fragment/EasasGlobalAlerts";
import ProductTemplateAttributeList from './ProductTemplateAttributesConfiguration/ProductTemplateAttributeList';
import ProductTemplateSettingsConfiguration from './ProductTemplateSettingsConfiguration/ProductTemplateSettingsConfiguration';
import { PRODUCT_TEMPLATE_AUTOCOMPLETE_MODEL } from "./autocomplete-model";

type TemplateTable = {
	id: bigint;
	refName: string;
	title: string;
};

type ConfiguratorProps = {
	templates: Array<TemplateTable>;
};

loader
	.init()
	.then(monaco => {
		monaco.languages.json.jsonDefaults.setDiagnosticsOptions(
			PRODUCT_TEMPLATE_AUTOCOMPLETE_MODEL,
		);
	})
	.catch(error =>
		console.error('An error occurred during initialization of Monaco: ', error),
	);

const defaultProductTemplate: IProductTemplate = {
	displayName: 'New Template Display Name',
	metaKeyColumn: '',
	safeName: 'company_some_table_name',
	keyColumn: 'key_column_safe_name',
	data: {
		attributes: [
			{
				displayName: 'Display name',
				type: CellType.STRING,
				referenceName: 'reference_name'
			}
		],
		parameterTemplates: [],
	},
};

const stringifyTemplate = (template: { [key: string]: any }) =>
	JSON.stringify(template, null, 2);

const ProductTemplatesConfiguratorJson = (props: ConfiguratorProps) => {
	const [selectedTemplate, setSelectedTemplate] =
		useState<TemplateTable | null>(null);
	const [templateConfigJson, setTemplateConfigJson] = useState<
		string | undefined
	>(undefined);
	const [template, setTemplate] = useState(defaultProductTemplate);

	useEffect(() => {
		if (selectedTemplate) {
			reloadConfig();
		}
	}, [selectedTemplate]);

	useEffect(() => {
		const timer = setTimeout(
			() => setTemplateConfigJson(stringifyTemplate(template)),
			500,
		);

		return () => {
			clearTimeout(timer);
		};
	}, [template]);

	const handleClear = () => {
		setSelectedTemplate(null);
		setTemplate(defaultProductTemplate);
		setTemplateConfigJson(stringifyTemplate(defaultProductTemplate));
	};

	const onTemplateChange = (e: DropdownChangeParams) => {
		setSelectedTemplate(e.target.value);
	};
	const onDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setTemplate(prev => ({ ...prev, displayName: e.target.value }));
	};
	const onRefNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		setTemplate(prev => ({ ...prev, safeName: e.target.value }));
	const onKeyAttrChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		setTemplate(prev => ({ ...prev, keyColumn: e.target.value }));
	const onMetaKeyChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		setTemplate(prev => ({ ...prev, metaKeyColumn: e.target.value }));

	const onAttributeChange = (attributes: IProductTemplateAttribute[]) => {
		setTemplate(prev => ({
			...prev,
			data: prev.data
				? { ...prev.data, attributes }
				: { attributes, parameterTemplates: [] },
		}));
	};

	const onConfigChange = (config: IProductTemplateConfig) => {
		setTemplate(prev => ({
			...prev,
			data: prev.data
				? { ...prev.data, config }
				: { attributes: [], parameterTemplates: [], config },
		}));
	};

	const reloadConfig = () => {
		ProductTemplatesApi.getProductTemplateConfig(
			selectedTemplate?.refName!,
		).then(t => {
			if (!t.error) {
				setTemplate(t.data!);
				setTemplateConfigJson(JSON.stringify(t.data, null, 2));
			} else {
				easasAlert({ summary: t.error.message!, detail: t.error.data, severity: "error" });
			}
		});
	};

	const updateConfig = () => {
		const ref = selectedTemplate?.refName;

		const parsedTemplate = checkJsonAndMergeConfig();
		if (!parsedTemplate) return;

		ProductTemplatesApi.updateProductTemplateConfig(ref!, parsedTemplate).then(
			r => {
				if (!r.error) {
					easasAlert(`Template "${r.data?.displayName}" updated`);
				} else {
					easasAlert({ summary: r.error.message!, detail: r.error.data, severity: "error" });
				}
			},
		);
	};

	const checkJsonAndMergeConfig = () => {
		try {
			const templateParsed = JSON.parse(templateConfigJson!);
			setTemplate(templateParsed);
			return templateParsed;
		} catch (ex: any) {
			easasAlert({ summary: `JSON Error: ${ex.message}`, severity: "error" });
			return null;
		}
	};

	const createTemplate = () => {
		const parsedTemplate = checkJsonAndMergeConfig();
		if (!parsedTemplate) return;

		ProductTemplatesApi.createProductTemplateConfig(parsedTemplate).then(r => {
			if (!r.error) {
				easasAlert(`Template "${r.data?.displayName}" created`);
			} else {
				easasAlert({ summary: r.error.message!, detail: r.error.data, severity: "error" });
			}
		});

		easasAlert('Template created');
	};

	const downloadProdTemplate = () => {
		const err = checkJsonAndMergeConfig();
		if (err) return;

		const fileName =
      selectedTemplate?.refName || `no_name_prod_template__${new Date().toISOString()}`;
		const json = JSON.stringify(template, null, 2);
		const blob = new Blob([json], { type: 'application/json' });
		const href = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = href;
		link.download = `${fileName  }.json`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const isTableSelected = selectedTemplate !== null;
	let submitButton;

	if (isTableSelected) {
		submitButton = (
			<Button
				label='Save'
				icon='pi pi-check'
				onClick={updateConfig}
				iconPos='right'
				className='p-m-2'
				style={{ width: '50%' }}
			/>
		);
	} else {
		submitButton = (
			<Button
				label='Create'
				icon='pi pi-plus'
				onClick={createTemplate}
				iconPos='right'
				className='p-button-success p-m-2'
				style={{ width: '50%' }}
			/>
		);
	}

	return (
		<div style={{ width: '100%', padding: '0 1rem 0 1rem' }}>
			<div className='p-fluid p-formgrid p-grid'>
				{/* ===== Table selection ======= */}
				<div className='p-field p-col-11'>
					<label htmlFor='type'>Template</label>
					<Dropdown
						value={selectedTemplate}
						options={props.templates}
						filter
						showClear
						filterBy='title'
						onChange={onTemplateChange}
						optionLabel='title'
						placeholder='Select a template'
					/>
				</div>
				{/* ===== Table selection ======= */}
				<div className='p-field p-col-1'>
					<label>Download</label>
					<Button
						icon='pi pi-download'
						style={{ width: '100%' }}
						onClickCapture={downloadProdTemplate}
					/>
				</div>
			</div>

			<TabView style={{ width: '100%' }}>
				<TabPanel header='Config'>
					<div className='p-fluid p-formgrid p-grid'>
						<div className='p-field p-col-12'>
							<label>Display name</label>
							<InputText
								placeholder='Display name'
								value={template?.displayName ?? ''}
								onChange={onDisplayNameChange}
								className='p-inputtext-sm'
							/>
						</div>
						<div className='p-field p-col-4'>
							<label>Id</label>
							<InputText
								placeholder='Not initialized'
								value={template?.id ?? ''}
								className='p-inputtext-sm'
								disabled
							/>
						</div>
						<div className='p-field p-col-4'>
							<label>Date created</label>
							<InputText
								placeholder='Date created'
								value={template?.dateCreated ?? ''}
								className='p-inputtext-sm'
								disabled
							/>
						</div>
						<div className='p-field p-col-4'>
							<label>Date modified</label>
							<InputText
								placeholder='Date modified'
								value={template?.dateModified ?? ''}
								className='p-inputtext-sm'
								disabled
							/>
						</div>
						<div className='p-field p-col-12'>
							<label>Reference name</label>
							<InputText
								placeholder='Reference name (is auto prefixed with company)'
								value={template?.safeName ?? ''}
								onChange={onRefNameChange}
								className='p-inputtext-sm'
								readOnly={isTableSelected}
							/>
						</div>
						<div className='p-field p-col-6'>
							<label>Key attribute</label>
							<InputText
								placeholder='Key attribute'
								value={template?.keyColumn ?? ''}
								onChange={onKeyAttrChange}
								className='p-inputtext-sm'
								disabled={false}
							/>
						</div>
						<div className='p-field p-col-6'>
							<label>
								Secondary key <i>(meta)</i> attribute
							</label>
							<InputText
								placeholder='Secondary key'
								value={template?.metaKeyColumn ?? ''}
								onChange={onMetaKeyChange}
								className='p-inputtext-sm'
								disabled={false}
							/>
						</div>

						<ProductTemplateAttributeList
							attributes={template?.data?.attributes!}
							onAttributesChange={onAttributeChange}
						/>
						<ProductTemplateSettingsConfiguration
							data={template.data!}
							onSettingsChange={onConfigChange}
						/>
					</div>
				</TabPanel>
				<TabPanel header='JSON'>
					<Editor
						height='400px'
						defaultLanguage='json'
						value={templateConfigJson}
						onChange={setTemplateConfigJson}
						theme='vs-dark'
					/>
				</TabPanel>
			</TabView>

			<div className='p-d-flex p-jc-between'>
				{submitButton}
				<Button
					label='Clear'
					icon='pi pi-trash'
					onClick={handleClear}
					iconPos='right'
					className='p-button-danger p-m-2'
					style={{ width: '50%' }}
				/>
			</div>
		</div>
	);
};


export default ProductTemplatesConfiguratorJson;


